import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Dialog } from '@angular/cdk/dialog';
import { Router } from '@angular/router';
import { CdkDrag, CdkDragEnd, CdkDragHandle } from '@angular/cdk/drag-drop';
import {
  KcButtonComponent,
  KcIconButtonComponent,
  KcIconComponent,
} from '@kaspacom/ui-kit';
import { WalletManager } from '../../managers/wallet.manager';
import { kaspaExplorerTxUrl } from '../../shared/kaspa-explorer';
import { shortId } from '../../shared/kcc20-formatters';
import {
  Kcc20OperationView,
  Kcc20WalletOperationQueueService,
} from '../../services/wallet-operations/kcc20-wallet-operation-queue.service';
import { WalletOperationRecoveryService } from '../../services/wallet-operations/wallet-operation-recovery.service';
import { KaspaComWebWalletService } from '../../services/wallets/kaspa-com-web-wallet.service';

// Bumped to /v1: the panel's default anchor moved from bottom-right to
// bottom-left (see the .scss), and an absolute-pixel position saved under
// the old key would otherwise reapply as a translate on top of the new
// anchor, visually dragging the panel back to its old spot for anyone who
// had ever repositioned it. The version bump makes those stale entries miss
// on lookup so the new default applies; fresh drags persist under the new
// key as before.
const DRAG_POSITION_STORAGE_KEY = 'kcc20-wallet-operation-overlay-position/v1';

function loadStoredDragPosition(): { x: number; y: number } {
  try {
    const raw = localStorage.getItem(DRAG_POSITION_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') {
      return { x: parsed.x, y: parsed.y };
    }
  } catch {
    // corrupt or inaccessible storage — fall back to the default position
  }
  return { x: 0, y: 0 };
}

@Component({
  selector: 'kcc20-wallet-operation-task-overlay',
  standalone: true,
  imports: [
    KcButtonComponent,
    KcIconButtonComponent,
    KcIconComponent,
    CdkDrag,
    CdkDragHandle,
  ],
  templateUrl: './wallet-operation-task-overlay.component.html',
  styleUrl: './wallet-operation-task-overlay.component.scss',
})
export class WalletOperationTaskOverlayComponent {
  protected readonly queue = inject(Kcc20WalletOperationQueueService);
  protected readonly recoveryService = inject(WalletOperationRecoveryService);
  private readonly router = inject(Router);
  protected readonly operations = this.queue.visibleOperations;
  protected readonly isCollapsed = signal(false);
  protected readonly dragPosition = signal(loadStoredDragPosition());

  private readonly overlayEl = viewChild<ElementRef<HTMLElement>>('overlayEl');
  private readonly cdkDialog = inject(Dialog);
  private readonly walletManager = inject(WalletManager);
  private readonly kaspaComWebWalletService = inject(KaspaComWebWalletService);

  /** Whether the KaspaCom web wallet iframe is currently the surface the
   * user must interact with (connect prompt, signing approval, etc). */
  private readonly isWalletSurfaceOpen = computed(() =>
    this.kaspaComWebWalletService.showWalletSignal()(),
  );

  private readonly isCdkDialogOpen = signal(
    this.cdkDialog.openDialogs.length > 0,
  );
  private readonly isAnyModalOpen = computed(
    () =>
      this.isCdkDialogOpen() ||
      this.walletManager.showLoginModalState() ||
      this.walletManager.showCookieConsentModal(),
  );
  /** Only set while a dialog/modal has forced a collapse — holds the user's
   * prior expand state so it can be restored once that modal closes. `null`
   * means nothing is currently force-collapsed. */
  private collapsedBeforeForce: boolean | null = null;

  constructor() {
    /* This overlay is intentionally rendered above every dialog's blur (see
     * the popover promotion below), so it must never visually cover an open
     * dialog/modal's content by accident. Nudging it down to its small pill
     * form when a dialog opens keeps that from happening in the common
     * case — but it's a default, not a lock: the user can still manually
     * re-expand via the toggle while the dialog stays open (toggleCollapsed
     * writes isCollapsed directly, which this effect leaves alone once the
     * one-time nudge has already fired). Whatever isCollapsed held right
     * before the nudge is restored once every modal is closed. */
    effect(() => {
      const modalOpen = this.isAnyModalOpen();
      if (modalOpen && this.collapsedBeforeForce === null) {
        this.collapsedBeforeForce = this.isCollapsed();
        this.isCollapsed.set(true);
      } else if (!modalOpen && this.collapsedBeforeForce !== null) {
        this.isCollapsed.set(this.collapsedBeforeForce);
        this.collapsedBeforeForce = null;
      }
    });

    this.cdkDialog.afterOpened
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.isCdkDialogOpen.set(true));
    this.cdkDialog.afterAllClosed
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.isCdkDialogOpen.set(false));

    /* Promotes the overlay into the browser's top layer via the Popover API
     * (popover="manual" in the template). kc-dialog's global backdrop-filter
     * blur (see .cdk-overlay-backdrop override) blurs this element despite
     * its high z-index — Chromium can squash it into the same paint layer as
     * the full-viewport blurred backdrop regardless of stacking order. Top
     * layer content is composited after and outside normal stacking
     * contexts entirely, so it's immune to that blur.
     *
     * The KaspaCom web wallet iframe is the one exception: it is *not*
     * top-layered, just a very-high-z-index element (see its SCSS). Normal
     * z-index can't out-rank top-layer content, so once this popover is
     * promoted it would sit above the iframe regardless of either one's
     * z-index — on mobile, where the iframe goes full-screen for connect
     * and signing prompts, that leaves this overlay eating clicks meant for
     * the wallet. Rather than race two elements for top-layer priority, we
     * fully demote the popover (hidePopover, which also drops it from the
     * render tree) for as long as the wallet surface is open, and
     * re-promote it once the wallet closes. */
    effect(() => {
      const el = this.overlayEl()?.nativeElement;
      if (!el) {
        return;
      }
      if (this.isWalletSurfaceOpen()) {
        if (el.matches(':popover-open')) {
          el.hidePopover();
        }
        return;
      }
      if (!el.matches(':popover-open')) {
        this.showTop(el);
      }
    });

    /* A popover's position in the top layer is fixed at the moment it's
     * shown — it does not get re-evaluated as later top-layer-adjacent
     * elements appear. A CDK dialog's backdrop (not itself top-layer, just a
     * very-high-z-index div) opened *after* our popover was already shown
     * still ends up blurring it: browsers don't correctly re-exclude an
     * already-promoted popover from a backdrop-filter created later. Cycling
     * the popover on every dialog open re-asserts it above that new
     * backdrop. Skipped while the wallet surface is open, since the popover
     * is intentionally hidden then (see effect above) and re-showing it
     * here would defeat that. */
    this.cdkDialog.afterOpened.pipe(takeUntilDestroyed()).subscribe(() => {
      const el = this.overlayEl()?.nativeElement;
      if (el?.matches(':popover-open') && !this.isWalletSurfaceOpen()) {
        setTimeout(() => this.showTop(el));
      }
    });
  }

  private showTop(el: HTMLElement): void {
    try {
      if (el.matches(':popover-open')) {
        el.hidePopover();
      }
      el.showPopover();
    } catch {
      // Ignored: benign if the browser lacks Popover API support — the
      // overlay still renders normally in flow, just not top-layered.
    }
  }

  protected readonly activeCount = computed(
    () => this.operations().filter((op) => this.isActive(op)).length,
  );
  protected readonly completedCount = computed(
    () => this.operations().filter((op) => op.phase === 'indexed').length,
  );
  protected readonly attentionCount = computed(
    () => this.operations().filter((op) => this.needsAttention(op)).length,
  );
  protected readonly summaryText = computed(() => {
    const active = this.activeCount();
    const attention = this.attentionCount();

    if (active && attention) {
      return `${active} active, ${attention} needs action`;
    }
    if (active) {
      return `${active} active`;
    }
    if (attention) {
      return `${attention} needs action`;
    }
    return `${this.completedCount()} completed`;
  });
  /**
   * Submitted (pending) operations — the ones whose tracking a sweep would
   * drop. Drives both the "Clear all" confirmation and its wording. Counts
   * every submitted op, not just `stalled` ones: since #221 a pending op can
   * be cleared at any point, so the confirmation has to cover all of them.
   */
  protected readonly submittedCount = computed(
    () => this.operations().filter((op) => op.phase === 'submitted').length,
  );
  protected readonly hasSubmitted = computed(() => this.submittedCount() > 0);
  /**
   * Whether "Clear all" has anything to do. Mirrors clearAll()'s own
   * predicate — done/needs-action/submitted, never a merely `dismissible`
   * queued/building/awaiting-signature op, which would otherwise let this
   * button silently cancel live wallet work.
   */
  protected readonly canClearAll = computed(() =>
    this.operations().some(
      (op) =>
        op.phase === 'submitted' ||
        op.phase === 'indexed' ||
        this.needsAttention(op),
    ),
  );
  protected readonly shortId = shortId;
  protected readonly explorerTxUrl = kaspaExplorerTxUrl;

  /**
   * Id of the submitted operation whose per-card "Stop tracking" is awaiting
   * the user's confirmation (#166), or null. Force-clearing loses local
   * tracking of a possibly still-processing transaction, so it must never be
   * one accidental click — the card flips to an explicit confirm step instead.
   * That confirmation is what lets the control be offered on ANY pending op
   * rather than only a stalled one (#221).
   */
  protected readonly confirmingStopTrackingId = signal<string | null>(null);
  /**
   * Whether the bulk "Clear all" action is awaiting confirmation. Only
   * needed when the sweep would drop tracking of a submitted operation (same
   * risk as above); a sweep of purely terminal cards runs immediately.
   */
  protected readonly confirmingClearAll = signal(false);

  protected toggleCollapsed(): void {
    this.isCollapsed.update((collapsed) => !collapsed);
    // Height changes after expand/collapse — re-clamp so Minimize / header
    // controls stay reachable instead of sitting off-screen.
    if (typeof window !== 'undefined') {
      window.setTimeout(() => this.clampStoredDragPosition(), 300);
    }
  }

  /**
   * Open the durable, backend-sourced record of past operations (#251). The
   * overlay is a live monitor — its cards can be dismissed and never survive
   * a reload — so it must point at the Portfolio Activity tab rather than try
   * to be a history view itself. (kc-button has no native routerLink.)
   */
  protected openHistory(): void {
    void this.router.navigate(['/portfolio'], {
      queryParams: { tab: 'activity' },
    });
  }

  protected onDragEnded(event: CdkDragEnd): void {
    const clamped = this.clampDragPosition(event.source.getFreeDragPosition());
    event.source.setFreeDragPosition(clamped);
    this.persistDragPosition(clamped);
  }

  private clampStoredDragPosition(): void {
    const clamped = this.clampDragPosition(this.dragPosition());
    this.persistDragPosition(clamped);
  }

  private persistDragPosition(position: { x: number; y: number }): void {
    this.dragPosition.set(position);
    try {
      localStorage.setItem(DRAG_POSITION_STORAGE_KEY, JSON.stringify(position));
    } catch {
      // storage unavailable (private mode / quota) — position just won't persist
    }
  }

  /**
   * Keep the overlay inside the viewport. If it is taller than the screen,
   * prefer the bottom edge (Minimize) so collapse stays reachable.
   */
  private clampDragPosition(position: { x: number; y: number }): {
    x: number;
    y: number;
  } {
    const el = this.overlayEl()?.nativeElement;
    if (!el || typeof window === 'undefined') {
      return position;
    }
    const rect = el.getBoundingClientRect();
    const margin = 8;
    let dx = 0;
    let dy = 0;

    if (rect.height >= window.innerHeight - margin * 2) {
      dy = window.innerHeight - margin - rect.bottom;
    } else {
      if (rect.top < margin) dy += margin - rect.top;
      if (rect.bottom > window.innerHeight - margin) {
        dy -= rect.bottom - (window.innerHeight - margin);
      }
    }

    if (rect.width >= window.innerWidth - margin * 2) {
      dx = window.innerWidth - margin - rect.right;
    } else {
      if (rect.left < margin) dx += margin - rect.left;
      if (rect.right > window.innerWidth - margin) {
        dx -= rect.right - (window.innerWidth - margin);
      }
    }

    if (dx === 0 && dy === 0) {
      return position;
    }
    return { x: position.x + dx, y: position.y + dy };
  }

  protected requestStopTracking(id: string): void {
    this.confirmingStopTrackingId.set(id);
  }

  protected cancelStopTracking(): void {
    this.confirmingStopTrackingId.set(null);
  }

  protected confirmStopTracking(id: string): void {
    this.queue.forceClearSubmitted(id);
    this.confirmingStopTrackingId.set(null);
  }

  protected requestClearAll(): void {
    if (this.hasSubmitted()) {
      this.confirmingClearAll.set(true);
      return;
    }
    this.queue.clearAll();
  }

  protected cancelClearAll(): void {
    this.confirmingClearAll.set(false);
  }

  protected confirmClearAll(): void {
    this.queue.clearAll();
    this.confirmingClearAll.set(false);
  }

  private isActive(op: Kcc20OperationView): boolean {
    return (
      op.phase === 'queued' ||
      op.phase === 'building' ||
      op.phase === 'awaiting-signature' ||
      op.phase === 'signing' ||
      op.phase === 'submitting' ||
      op.phase === 'submitted'
    );
  }

  private needsAttention(op: Kcc20OperationView): boolean {
    return (
      op.phase === 'failed' ||
      op.phase === 'rejected' ||
      op.phase === 'untracked'
    );
  }
}
